import { useEffect, useState, useCallback } from 'react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Legend,
} from 'recharts';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import api from '../services/api';

const fmt     = (n) => Number(n || 0).toLocaleString('fr-FR');
const PERIODS = [
  { key: 'weekly',    label: 'Hebdomadaire', icon: '📅' },
  { key: 'monthly',   label: 'Mensuel',      icon: '🗓️' },
  { key: 'quarterly', label: 'Trimestriel',  icon: '📆' },
  { key: 'annual',    label: 'Annuel',       icon: '📊' },
];

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px', boxShadow: 'var(--shadow-md)', minWidth: 160 }}>
      <p style={{ fontWeight: 700, marginBottom: 6, fontSize: 13 }}>{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color, fontSize: 12, fontWeight: 600, marginBottom: 2 }}>
          {p.name}: {p.name.toLowerCase().includes('rev') ? `${fmt(p.value)} F` : fmt(p.value)}
        </p>
      ))}
    </div>
  );
};

const KpiTile = ({ label, value, icon, color, sub }) => (
  <div style={{ background: 'var(--surface)', borderRadius: 12, padding: '20px 22px', borderTop: `3px solid ${color}`, boxShadow: 'var(--shadow-sm)' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
      <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.5px' }}>{label}</span>
      <span style={{ fontSize: 20 }}>{icon}</span>
    </div>
    <div style={{ fontSize: 26, fontWeight: 800, color }}>{value}</div>
    {sub && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>{sub}</div>}
  </div>
);

export default function Reports() {
  const [period, setPeriod]   = useState('monthly');
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(null); // 'excel' | 'pdf' | null

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/admin/reports?period=${period}`)
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, [period]);

  useEffect(() => { load(); }, [load]);

  const rows = data?.rows ?? [];

  // Totaux sur la période affichée
  const totals = rows.reduce((acc, r) => ({
    commission_revenue:    acc.commission_revenue    + r.commission_revenue,
    subscription_revenue:  acc.subscription_revenue  + r.subscription_revenue,
    total_revenue:         acc.total_revenue         + r.total_revenue,
    new_users:             acc.new_users             + r.new_users,
    deals_closed:          acc.deals_closed          + r.deals_closed,
    commission_count:      acc.commission_count      + r.commission_count,
  }), { commission_revenue: 0, subscription_revenue: 0, total_revenue: 0, new_users: 0, deals_closed: 0, commission_count: 0 });

  // Graphique des revenus (barres empilées)
  const revenueChart = [...rows].reverse().map(r => ({
    name:          r.period,
    'Abonnements': r.subscription_revenue,
    'Commissions': r.commission_revenue,
  }));

  // Graphique activité (lignes)
  const activityChart = [...rows].reverse().map(r => ({
    name:          r.period,
    'Nouveaux utilisateurs': r.new_users,
    'Deals conclus':         r.deals_closed,
    'Transactions':          r.commission_count,
  }));

  const periodLabel = PERIODS.find(p => p.key === period)?.label || period;
  const todayStamp  = new Date().toLocaleDateString('fr-FR');
  const fileBase    = `Rapport-Elite-${period}-${new Date().toISOString().slice(0,10)}`;

  // ── EXPORT EXCEL ───────────────────────────────────────────────
  function exportExcel() {
    if (!rows.length) return;
    setExporting('excel');
    try {
      const wb = XLSX.utils.book_new();

      // Feuille 1 : Synthèse
      const synth = [
        ['Rapport Elite Services'],
        [`Période : ${periodLabel}`],
        [`Généré le : ${todayStamp}`],
        [],
        ['Indicateur', 'Valeur'],
        ['Revenus totaux',           totals.total_revenue],
        ['Revenus abonnements',      totals.subscription_revenue],
        ['Revenus commissions',      totals.commission_revenue],
        ['Nouveaux utilisateurs',    totals.new_users],
        ['Deals conclus',            totals.deals_closed],
        ['Nombre de transactions',   totals.commission_count],
      ];
      const wsSynth = XLSX.utils.aoa_to_sheet(synth);
      wsSynth['!cols'] = [{ wch: 32 }, { wch: 22 }];
      // Formatage monétaire pour les lignes revenus (cellules B6..B8)
      ['B6','B7','B8'].forEach(addr => {
        if (wsSynth[addr]) wsSynth[addr].z = '#,##0 "FCFA"';
      });
      XLSX.utils.book_append_sheet(wb, wsSynth, 'Synthèse');

      // Feuille 2 : Détail par période
      const header = ['Période', 'Abonnements (FCFA)', 'Commissions (FCFA)', 'Total revenus (FCFA)', 'Nouveaux utilisateurs', 'Deals conclus', 'Transactions'];
      const body = rows.map(r => [
        r.period,
        Number(r.subscription_revenue) || 0,
        Number(r.commission_revenue) || 0,
        Number(r.total_revenue) || 0,
        Number(r.new_users) || 0,
        Number(r.deals_closed) || 0,
        Number(r.commission_count) || 0,
      ]);
      const totalRow = [
        'TOTAL',
        totals.subscription_revenue,
        totals.commission_revenue,
        totals.total_revenue,
        totals.new_users,
        totals.deals_closed,
        totals.commission_count,
      ];
      const wsDet = XLSX.utils.aoa_to_sheet([header, ...body, totalRow]);
      wsDet['!cols'] = [{ wch: 14 }, { wch: 18 }, { wch: 18 }, { wch: 20 }, { wch: 22 }, { wch: 16 }, { wch: 16 }];
      // Format monétaire colonnes B, C, D (indices 1, 2, 3) pour toutes les lignes data + total
      const nRows = body.length + 1; // header + body + total = body.length+2; index dernière ligne = body.length+1
      for (let r = 1; r <= nRows; r++) {
        ['B','C','D'].forEach(col => {
          const addr = `${col}${r + 1}`;
          if (wsDet[addr]) wsDet[addr].z = '#,##0';
        });
      }
      XLSX.utils.book_append_sheet(wb, wsDet, 'Détail');

      XLSX.writeFile(wb, `${fileBase}.xlsx`);
    } finally {
      setExporting(null);
    }
  }

  // ── EXPORT PDF ─────────────────────────────────────────────────
  function exportPDF() {
    if (!rows.length) return;
    setExporting('pdf');
    try {
      const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
      const pageW = doc.internal.pageSize.getWidth();

      // En-tête
      doc.setFillColor(13, 52, 128);
      doc.rect(0, 0, pageW, 60, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('Elite Services — Rapport périodique', 40, 28);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.text(`Période : ${periodLabel}  ·  Généré le : ${todayStamp}`, 40, 48);

      // Bloc KPIs
      doc.setTextColor(20, 20, 30);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Synthèse', 40, 90);

      const kpis = [
        ['Revenus totaux',         `${fmt(totals.total_revenue)} FCFA`],
        ['  · Abonnements',        `${fmt(totals.subscription_revenue)} FCFA`],
        ['  · Commissions',        `${fmt(totals.commission_revenue)} FCFA`],
        ['Nouveaux utilisateurs',  fmt(totals.new_users)],
        ['Deals conclus',          fmt(totals.deals_closed)],
        ['Nombre de transactions', fmt(totals.commission_count)],
      ];
      autoTable(doc, {
        startY: 100,
        head:  [['Indicateur', 'Valeur']],
        body:  kpis,
        theme: 'grid',
        headStyles: { fillColor: [241, 245, 249], textColor: [51, 65, 85], fontStyle: 'bold' },
        styles: { fontSize: 10, cellPadding: 6 },
        columnStyles: { 0: { cellWidth: 220 }, 1: { halign: 'right', fontStyle: 'bold' } },
        margin: { left: 40, right: 40 },
      });

      // Tableau détaillé
      const startY = doc.lastAutoTable.finalY + 24;
      doc.setFont('helvetica', 'bold');
      doc.text(`Détail — ${periodLabel}`, 40, startY);

      const body = rows.map(r => [
        r.period,
        `${fmt(r.subscription_revenue)}`,
        `${fmt(r.commission_revenue)}`,
        `${fmt(r.total_revenue)}`,
        fmt(r.new_users),
        fmt(r.deals_closed),
        fmt(r.commission_count),
      ]);
      const totalRow = [
        'TOTAL',
        fmt(totals.subscription_revenue),
        fmt(totals.commission_revenue),
        fmt(totals.total_revenue),
        fmt(totals.new_users),
        fmt(totals.deals_closed),
        fmt(totals.commission_count),
      ];

      autoTable(doc, {
        startY: startY + 10,
        head: [['Période', 'Abonnements\n(FCFA)', 'Commissions\n(FCFA)', 'Total revenus\n(FCFA)', 'Nouveaux\nutilisateurs', 'Deals\nconclus', 'Transactions']],
        body,
        foot: [totalRow],
        theme: 'striped',
        headStyles:  { fillColor: [13, 52, 128], textColor: 255, fontStyle: 'bold', halign: 'center' },
        footStyles:  { fillColor: [241, 245, 249], textColor: [13, 52, 128], fontStyle: 'bold' },
        bodyStyles:  { fontSize: 9 },
        columnStyles: {
          0: { halign: 'left',  fontStyle: 'bold' },
          1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right', fontStyle: 'bold' },
          4: { halign: 'right' }, 5: { halign: 'right' }, 6: { halign: 'right' },
        },
        margin: { left: 40, right: 40 },
      });

      // Pied de page
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(140, 140, 140);
        doc.text(`Elite Services — Page ${i}/${pageCount}`, pageW - 40, doc.internal.pageSize.getHeight() - 16, { align: 'right' });
      }

      doc.save(`${fileBase}.pdf`);
    } finally {
      setExporting(null);
    }
  }

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 className="page-title">Rapports périodiques</h2>
          <p className="page-subtitle">Analyse des performances sur la période sélectionnée</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            onClick={exportExcel}
            disabled={!rows.length || exporting !== null}
            title="Télécharger en Excel (.xlsx)"
            style={{
              background: '#10b981', color: '#fff', border: 'none', borderRadius: 8,
              padding: '9px 16px', fontSize: 13, fontWeight: 700, cursor: rows.length && !exporting ? 'pointer' : 'not-allowed',
              opacity: rows.length && exporting !== 'excel' ? 1 : 0.6, display: 'inline-flex',
              alignItems: 'center', gap: 6, transition: 'all .15s',
            }}
          >
            {exporting === 'excel' ? '⏳ Génération…' : '📊 Excel'}
          </button>
          <button
            onClick={exportPDF}
            disabled={!rows.length || exporting !== null}
            title="Télécharger en PDF"
            style={{
              background: '#dc2626', color: '#fff', border: 'none', borderRadius: 8,
              padding: '9px 16px', fontSize: 13, fontWeight: 700, cursor: rows.length && !exporting ? 'pointer' : 'not-allowed',
              opacity: rows.length && exporting !== 'pdf' ? 1 : 0.6, display: 'inline-flex',
              alignItems: 'center', gap: 6, transition: 'all .15s',
            }}
          >
            {exporting === 'pdf' ? '⏳ Génération…' : '📄 PDF'}
          </button>
          <button onClick={load} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', cursor: 'pointer', fontSize: 16 }} title="Rafraîchir">🔄</button>
        </div>
      </div>

      {/* ── Sélecteur de période ── */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 24, flexWrap: 'wrap' }}>
        {PERIODS.map(p => (
          <button
            key={p.key}
            onClick={() => setPeriod(p.key)}
            style={{
              padding: '10px 20px', borderRadius: 'var(--radius-full)',
              fontSize: 13, fontWeight: 700, cursor: 'pointer',
              border: period === p.key ? '2px solid var(--primary)' : '1.5px solid var(--border)',
              background: period === p.key ? 'var(--primary)' : 'var(--surface)',
              color: period === p.key ? '#fff' : 'var(--text-secondary)',
              transition: 'all .15s',
            }}
          >
            {p.icon} {p.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <div className="spinner" />
        </div>
      ) : (
        <>
          {/* ── KPIs totaux ── */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24 }}>
            <KpiTile
              label="Revenus totaux"
              value={`${fmt(totals.total_revenue)} F`}
              icon="💰"
              color="var(--primary)"
              sub={`Abonnements : ${fmt(totals.subscription_revenue)} F · Commissions : ${fmt(totals.commission_revenue)} F`}
            />
            <KpiTile
              label="Nouveaux utilisateurs"
              value={fmt(totals.new_users)}
              icon="👥"
              color="var(--info)"
            />
            <KpiTile
              label="Deals conclus"
              value={fmt(totals.deals_closed)}
              icon="🤝"
              color="var(--success)"
              sub={`${fmt(totals.commission_count)} transaction(s)`}
            />
          </div>

          {/* ── Graphique revenus ── */}
          <div className="card animate-fade" style={{ marginBottom: 24 }}>
            <div className="card-header">
              <span className="card-title">💰 Évolution des revenus</span>
            </div>
            <div className="card-body">
              {rows.length === 0 ? (
                <p style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '32px 0' }}>Aucune donnée pour cette période</p>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={revenueChart} barSize={24}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} axisLine={false} tickLine={false} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                    <Tooltip content={<ChartTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="Abonnements" stackId="rev" fill="#3b82f6" radius={[0,0,0,0]} />
                    <Bar dataKey="Commissions" stackId="rev" fill="#e94560" radius={[4,4,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* ── Graphique activité ── */}
          <div className="card animate-fade" style={{ marginBottom: 24, animationDelay: '.1s' }}>
            <div className="card-header">
              <span className="card-title">📈 Activité de la plateforme</span>
            </div>
            <div className="card-body">
              {rows.length === 0 ? (
                <p style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '32px 0' }}>Aucune donnée</p>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={activityChart}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} axisLine={false} tickLine={false} />
                    <Tooltip content={<ChartTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Line type="monotone" dataKey="Nouveaux utilisateurs" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                    <Line type="monotone" dataKey="Deals conclus"         stroke="#10b981" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                    <Line type="monotone" dataKey="Transactions"          stroke="#f59e0b" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* ── Tableau détaillé ── */}
          <div className="card animate-fade" style={{ animationDelay: '.2s' }}>
            <div className="card-header">
              <span className="card-title">📋 Tableau détaillé — {PERIODS.find(p => p.key === period)?.label}</span>
            </div>
            <div style={{ overflowX: 'auto' }}>
              {rows.length === 0 ? (
                <div style={{ padding: '40px 24px', textAlign: 'center', color: 'var(--text-muted)' }}>
                  <div style={{ fontSize: 40, marginBottom: 12 }}>📭</div>
                  <div>Aucune donnée pour cette période</div>
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--border)', background: 'var(--surface-2)' }}>
                      {['Période','Abonnements','Commissions','Total revenus','Nouveaux utilisateurs','Deals conclus','Transactions'].map(h => (
                        <th key={h} style={{ padding: '12px 16px', textAlign: h === 'Période' ? 'left' : 'right', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.5px', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr
                        key={i}
                        style={{ borderBottom: '1px solid var(--border)', transition: 'background .1s' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
                        onMouseLeave={e => e.currentTarget.style.background = ''}
                      >
                        <td style={{ padding: '12px 16px', fontWeight: 700, color: 'var(--text-primary)' }}>{r.period}</td>
                        <td style={{ padding: '12px 16px', textAlign: 'right', color: '#3b82f6', fontWeight: 600 }}>{fmt(r.subscription_revenue)} F</td>
                        <td style={{ padding: '12px 16px', textAlign: 'right', color: '#e94560', fontWeight: 600 }}>{fmt(r.commission_revenue)} F</td>
                        <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 800, color: 'var(--text-primary)' }}>{fmt(r.total_revenue)} F</td>
                        <td style={{ padding: '12px 16px', textAlign: 'right', color: '#8b5cf6', fontWeight: 600 }}>{fmt(r.new_users)}</td>
                        <td style={{ padding: '12px 16px', textAlign: 'right', color: '#10b981', fontWeight: 600 }}>{fmt(r.deals_closed)}</td>
                        <td style={{ padding: '12px 16px', textAlign: 'right', color: '#f59e0b', fontWeight: 600 }}>{fmt(r.commission_count)}</td>
                      </tr>
                    ))}
                    {/* Ligne totaux */}
                    <tr style={{ borderTop: '2px solid var(--border)', background: 'var(--surface-2)' }}>
                      <td style={{ padding: '12px 16px', fontWeight: 800, fontSize: 13 }}>TOTAL</td>
                      <td style={{ padding: '12px 16px', textAlign: 'right', color: '#3b82f6', fontWeight: 800 }}>{fmt(totals.subscription_revenue)} F</td>
                      <td style={{ padding: '12px 16px', textAlign: 'right', color: '#e94560', fontWeight: 800 }}>{fmt(totals.commission_revenue)} F</td>
                      <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 800, color: 'var(--primary)', fontSize: 15 }}>{fmt(totals.total_revenue)} F</td>
                      <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 800 }}>{fmt(totals.new_users)}</td>
                      <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 800 }}>{fmt(totals.deals_closed)}</td>
                      <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 800 }}>{fmt(totals.commission_count)}</td>
                    </tr>
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
